/**
 * Admin panel frame — same "Frost" look as the web app (D88): a labelled left sidebar on PCs;
 * on phones and tablets round icon buttons that scroll sideways under the logo.
 */
import type { AdminPermission } from "@custos/core";
import { Badge, Logo, NetworkBadge, cn } from "@custos/ui";
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  BarChart3,
  CircleUser,
  LayoutDashboard,
  LogOut,
  Megaphone,
  MessagesSquare,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, useLocation, useNavigate } from "react-router";
import { api, queryClient } from "../lib/api";
import { useCan } from "../lib/permissions";
import { useConfig, useOverview } from "../lib/queries";

/** Pages and the permissions that open them (any one is enough; empty = everyone). Shared with App routes. */
export const NAV: { to: string; label: string; icon: LucideIcon; perms: AdminPermission[] }[] = [
  { to: "/", label: "Overview", icon: LayoutDashboard, perms: ["dashboard.view"] },
  { to: "/withdrawals", label: "Withdrawals", icon: ArrowUpRight, perms: ["queues.view"] },
  { to: "/deposits", label: "Deposits", icon: ArrowDownLeft, perms: ["queues.view"] },
  { to: "/swaps", label: "Swaps", icon: ArrowLeftRight, perms: ["swaps.view"] },
  { to: "/users", label: "Users", icon: Users, perms: ["users.view"] },
  { to: "/wallets", label: "Wallets", icon: Wallet, perms: ["wallets.view"] },
  {
    to: "/vault-recovery",
    label: "Vault recovery",
    icon: ShieldCheck,
    perms: ["vault_recovery.view", "vault_recovery.decide"],
  },
  {
    to: "/limits",
    label: "Limits",
    icon: SlidersHorizontal,
    perms: ["dashboard.view", "emergency.pause", "limits.edit"],
  },
  { to: "/audit", label: "Audit log", icon: ScrollText, perms: ["audit.view"] },
  { to: "/reports", label: "Reports", icon: BarChart3, perms: ["reports.view"] },
  { to: "/support", label: "Support", icon: MessagesSquare, perms: ["support.chat"] },
  { to: "/messages", label: "Messages", icon: Megaphone, perms: ["messages.manage"] },
  { to: "/staff", label: "Staff", icon: UserCog, perms: ["staff.manage"] },
  { to: "/account", label: "My account", icon: CircleUser, perms: [] },
];

export function useLogout() {
  const navigate = useNavigate();
  return async () => {
    await api.logout();
    queryClient.clear();
    navigate("/login", { replace: true });
  };
}

export function AdminShell({ children }: { children: ReactNode }) {
  const config = useConfig();
  const can = useCan();
  const overview = useOverview(can("dashboard.view"));
  const nav = NAV.filter((item) => item.perms.length === 0 || can(...item.perms));
  // Conversations with unread customer messages (D79), every 30 s.
  const supportUnread = useQuery({
    queryKey: ["support-unread"],
    queryFn: () => api.supportUnread(),
    refetchInterval: 30_000,
    enabled: can("support.chat"),
  });
  const logout = useLogout();
  const location = useLocation();
  const pending = overview.data?.pendingApproval ?? 0;

  return (
    <div className="flex min-h-dvh flex-col lg:pl-64">
      <header className="sticky top-0 z-30 backdrop-blur-xl lg:fixed lg:inset-y-4 lg:left-4 lg:w-56">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6 lg:glass lg:h-full lg:flex-col lg:flex-nowrap lg:items-stretch lg:gap-4 lg:rounded-[28px] lg:p-4">
          <div className="flex items-center gap-2 lg:px-2 lg:pt-1">
            <Logo />
            <Badge tone="primary">Admin</Badge>
          </div>
          <nav
            aria-label="Admin navigation"
            data-testid="adminnav"
            className="order-last -mx-4 flex w-[calc(100%+2rem)] gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:w-full sm:px-0 lg:order-none lg:mx-0 lg:w-auto lg:flex-1 lg:flex-col lg:gap-0.5 lg:overflow-y-auto lg:pb-0"
          >
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                title={item.label}
                aria-label={item.label}
                className={({ isActive }) =>
                  cn(
                    "relative inline-flex size-11 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors duration-200 lg:h-10 lg:w-full lg:justify-start lg:gap-3 lg:px-3.5",
                    isActive
                      ? "bg-primary-gradient lg:bg-surface lg:bg-none lg:font-semibold lg:text-primary-strong lg:shadow-sm"
                      : "glass-circle lg:border-0 lg:bg-transparent lg:text-muted lg:shadow-none lg:hover:bg-hover lg:hover:text-fg",
                  )
                }
              >
                <item.icon className="size-5 shrink-0" aria-hidden />
                <span className="hidden truncate lg:inline" aria-hidden>
                  {item.label}
                </span>
                {item.to === "/support" && (supportUnread.data ?? 0) > 0 && (
                  <span
                    data-testid="support-unread"
                    className="absolute -top-0.5 -right-0.5 inline-flex min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-white lg:static lg:ml-auto"
                  >
                    {supportUnread.data}
                  </span>
                )}
                {item.to === "/withdrawals" && pending > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 inline-flex min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-white lg:static lg:ml-auto">
                    {pending}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2 lg:ml-0 lg:justify-between lg:px-1">
            {config.data && <NetworkBadge label={config.data.badge} />}
            <button
              type="button"
              onClick={logout}
              title="Log out"
              aria-label="Log out"
              className="glass-circle inline-flex size-11 items-center justify-center rounded-full"
            >
              <LogOut className="size-5" aria-hidden />
            </button>
          </div>
        </div>
      </header>

      {overview.data?.withdrawalsPaused && (
        <div role="alert" className="bg-danger/15 px-4 py-2 text-center text-sm font-medium text-danger">
          ALL WITHDRAWALS ARE PAUSED
        </div>
      )}

      {/* Solid white panels here: tables and queues read better without the frosted effect. */}
      <main
        key={location.pathname}
        className="mx-auto w-full max-w-7xl flex-1 animate-fade px-4 pt-4 pb-16 [--glass-panel:var(--surface)] sm:px-6 lg:pt-6 lg:pr-8 lg:pl-4"
      >
        {children}
      </main>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3 sm:mb-6">
      <div className="min-w-0">
        <h1 className="font-heading text-[28px] leading-tight font-semibold tracking-[-0.03em] text-fg sm:text-4xl">
          {title}
        </h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
